'use client';

import { BN } from '@drift-labs/sdk';
import {
	BASE_PRECISION_EXP,
	BigNum,
	PositionDirection,
	SLOT_TIME_ESTIMATE_MS,
	SpotMarketConfig,
} from '@drift-labs/sdk';
import { ENUM_UTILS, MarketId, UIMarket } from '@drift/common';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MSG_SIGNING_BEFORE_AUCTION_END_SLOT_BUFFER } from '../../../constants/signedOrderMsgs';
import { useCurrentSlot } from 'src/hooks/useCurrentSlot';
import {
	NotificationType,
	V2AuctionToastDataBody,
} from '../../../utils/notifications';
import {
	MarketOrderToastState,
	V3MarketOrderHeadlineStage,
} from '../../MarketOrderToasts/MarketOrderToastStateTypes';
import Toast from '../Toast';
import {
	TOAST_CONTENT_CONTAINER_CLASSNAME,
	TOAST_CONTENT_CONTAINER_STYLE,
	TOAST_SUB_CONTENT_CLASSNAME,
} from 'src/components/Toasts/consts';

const useForceRender = () => {
	const [_, forceRender] = useState(0);
	useEffect(() => {
		const interval = setInterval(() => {
			forceRender((prev) => prev + 1);
		}, 1000);
		return () => clearInterval(interval);
	}, []);
	return _;
};

const AWAITING_SIGNATURE_SLOT_TIMEOUT = 50; // Usually a transaction will expire after 150 slots (see SOLANA_TRANSACTION_BLOCK_HEIGHT_EXPIRY), but we want to encourage them to sign the transaction faster and also to make the component look more active.

const useToastTypeProps = ({
	stage,
	data,
}: {
	stage: V3MarketOrderHeadlineStage;
	data: MarketOrderToastState;
}): {
	type: Exclude<NotificationType, 'alert'>;
	timeoutStartMs?: number;
	timeoutEndMs?: number;
	progressStart?: number;
	progressEnd?: number;
} => {
	const startTimeMs = useRef(Date.now()); // Store the start time when we first rendered this component
	const endTimeMs = useMemo(() => {
		const startSlot = data.orderInfo.toastInitBlockHeight;
		const endSlot = (startSlot ?? 0) + AWAITING_SIGNATURE_SLOT_TIMEOUT;

		return (
			startTimeMs.current + (endSlot - (startSlot ?? 0)) * SLOT_TIME_ESTIMATE_MS
		);
	}, [data.orderInfo.toastInitBlockHeight, data.orderInfo.orderRoute]); // The end time depends on the initial block height. This may not be available upon first render.

	return useMemo(() => {
		switch (stage) {
			case 'awaiting_signature': {
				if (data.orderInfo.orderRoute === 'signedMsg') {
					return {
						type: data.orderInfo.isAutoSigned
							? 'awaiting'
							: 'progress_indicator',
						progressStart: data.orderInfo.auctionStartSlot,
						progressEnd:
							(data.orderInfo.auctionStartSlot ?? 0) +
							(data.orderInfo.auctionDuration ?? 0) -
							MSG_SIGNING_BEFORE_AUCTION_END_SLOT_BUFFER,
					};
				}

				return {
					type: 'timeout',
					timeoutStartMs: startTimeMs.current,
					timeoutEndMs: endTimeMs,
				};
			}
			case 'awaiting_confirmation':
			case 'awaiting_fill':
			case 'filling_partial':
				return {
					type: 'awaiting',
				};
			case 'failed_signature':
			case 'failed_confirmation':
			case 'not_filled_and_cancelled':
			case 'not_filled_and_error':
			case 'not_filled_and_expired':
				return {
					type: 'error',
				};
			case 'partial_filled_and_complete':
				return {
					type: 'warning',
				};
			case 'filled_and_complete':
				return {
					type: 'success',
				};
			default: {
				const _unhandledStage: never = stage;
				throw new Error(`Unhandled stage: ${_unhandledStage}`);
			}
		}
	}, [stage, data]);
};

const StatusMessage = ({
	stage,
	marketId,
	avgFillPrice,
	sizeFilled,
	sizeOrdered,
	direction,
	customSignMessage,
}: {
	marketId: MarketId;
	stage: V3MarketOrderHeadlineStage;
	avgFillPrice: string;
	sizeFilled: BN;
	sizeOrdered: BN;
	direction: PositionDirection;
	customSignMessage?: string;
}) => {
	let message: React.ReactNode;

	const uiMarket = UIMarket.fromMarketId(marketId);

	const basePrecision = useMemo(() => {
		return marketId.isPerp
			? BASE_PRECISION_EXP
			: (uiMarket.market as SpotMarketConfig).precisionExp;
	}, [marketId]);

	const baseAmountFilled = useMemo(() => {
		return BigNum.from(sizeFilled, basePrecision).toNum();
	}, [basePrecision, sizeFilled]);

	const baseAmountOrdered = useMemo(() => {
		return BigNum.from(sizeOrdered, basePrecision).toNum();
	}, [basePrecision, sizeOrdered]);

	const boughtOrSold = ENUM_UTILS.match(direction, PositionDirection.LONG)
		? 'bought'
		: 'sold';

	switch (stage) {
		case 'awaiting_signature':
			message = customSignMessage ?? 'Confirm the transaction in your wallet';
			break;
		case 'awaiting_confirmation':
			message = 'Awaiting confirmation...';
			break;
		case 'awaiting_fill':
			message = 'Order submitted...';
			break;
		case 'failed_signature':
			message = 'Failed to sign.';
			break;
		case 'failed_confirmation':
			message = 'Transaction failed to confirm.';
			break;
		case 'not_filled_and_cancelled':
			message = 'Order cancelled.';
			break;
		case 'not_filled_and_error':
			message = 'Order failed.';
			break;
		case 'not_filled_and_expired':
			message = 'Order expired.';
			break;
		case 'filling_partial':
		case 'partial_filled_and_complete':
			message = `${baseAmountFilled}/${baseAmountOrdered} ${uiMarket.symbol} ${boughtOrSold} at average price ${avgFillPrice}`;
			break;
		case 'filled_and_complete':
			message = `${baseAmountFilled} ${uiMarket.symbol} ${boughtOrSold} at average price ${avgFillPrice}`;
			break;
		default: {
			const _unhandledStage: never = stage;
			throw new Error(`Unhandled stage: ${_unhandledStage}`);
		}
	}

	return message;
};

const V3MarketOrderAlert = ({
	data,
	onClose,
}: {
	data: V2AuctionToastDataBody;
	onClose: () => void;
}) => {
	// TODO can we remove this? let's try
	useForceRender();
	const currentSlot = useCurrentSlot();

	const { type, timeoutStartMs, timeoutEndMs, progressStart, progressEnd } =
		useToastTypeProps({
			stage: data.v3HeadlineStage,
			data,
		});

	return (
		<Toast
			type={type}
			onClose={onClose}
			timeoutStartMs={timeoutStartMs}
			timeoutEndMs={timeoutEndMs}
			progressStart={progressStart}
			progressEnd={progressEnd}
			progressCurrent={currentSlot}
		>
			<div
				className={TOAST_CONTENT_CONTAINER_CLASSNAME}
				style={TOAST_CONTENT_CONTAINER_STYLE}
			>
				<StatusMessage
					marketId={data.orderInfo.marketId}
					stage={data.v3HeadlineStage}
					avgFillPrice={data.orderInfo.avgFillPriceNotionalString ?? ''}
					sizeFilled={data.orderInfo.cumulativeBaseAmountFilled}
					sizeOrdered={data.orderInfo.baseAmountOrdered}
					direction={data.orderInfo.direction}
					customSignMessage={data.orderInfo.customSignMessage}
				/>
				{data.orderInfo.failureMessage && (
					<span className={TOAST_SUB_CONTENT_CLASSNAME}>
						{data.orderInfo.failureMessage}
					</span>
				)}
			</div>
		</Toast>
	);
};

export default V3MarketOrderAlert;
